import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Sparkles,
  Activity,
  RefreshCw,
  AlertTriangle,
  ShieldCheck,
  ChevronRight,
  Lightbulb,
  Layers,
  Settings,
} from 'lucide-react';
import { AdminLayout } from '../../components/Admin/AdminLayout';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../../store/authStore';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000';

type Range = 'today' | 'yesterday' | '7d' | '30d';
type Severity = 'critical' | 'high' | 'medium' | 'low';
type Direction = 'increase' | 'decrease' | 'enable' | 'disable' | 'review';

interface Recommendation {
  id: string;
  scope: 'global' | 'page';
  key: string | null;
  direction: Direction;
  currentValue: unknown;
  suggestedValue: unknown;
  severity: Severity;
  confidence: 'high' | 'medium' | 'low';
  evidenceCount: number;
  evidencePct: number;
  rationaleKey: string;
  rationaleVars: Record<string, unknown>;
}

interface PageRow {
  id: string;
  name: string;
  slug: string | null;
  ownerName: string;
  ownerEmail: string | null;
  cloakingEnabled: boolean;
  allowedCountries: string[];
  summary: {
    totalBlocks: number;
    views: number;
    totalHits: number;
    blockRate: number;
    falsePositiveCount: number;
    falsePositiveRate: number;
  };
  recommendations: Recommendation[];
}

interface GlobalOpportunity {
  key: string;
  direction: Direction;
  currentValue: unknown;
  suggestedValue: unknown;
  severity: Severity;
  totalEvidence: number;
  affectedPages: number;
  pages: Array<{
    id: string;
    name: string;
    ownerEmail: string | null;
    evidenceCount: number;
    rationaleKey: string;
    rationaleVars: Record<string, unknown>;
  }>;
}

interface RecommendationsResponse {
  range: Range;
  startDate: string;
  endDate: string;
  pages: PageRow[];
  globalOpportunities: GlobalOpportunity[];
}

/**
 * Super-admin tuning advisor.
 *
 * Surfaces — but does NOT auto-apply — concrete suggestions for what
 * cloaking knob to turn for which page. This is "level 1" of the
 * auto-tune roadmap; the eventual level-2 will apply the ones flagged
 * here automatically with safety rails.
 *
 * Two sections:
 *   1. Global opportunities — config tweaks that would help multiple
 *      pages at once (e.g. "bump visitor_rep_pages from 5 → 10 helps
 *      4 pages with 142 false-positive blocks total"). One click
 *      jumps to the global thresholds editor with the suggested
 *      value pre-filled in the URL hash.
 *   2. Per-page details — every page that has at least one rec,
 *      sorted by severity. Each page row links to its detail view
 *      where the same recs are shown alongside the editor.
 */
export const AdminCloakingRecommendations: React.FC = () => {
  const { t } = useTranslation('admin');
  const token = useAuthStore((s) => s.token);
  const navigate = useNavigate();
  const [range, setRange] = useState<Range>('7d');
  const [data, setData] = useState<RecommendationsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchData = async (showSpinner = true) => {
    if (!token) return;
    if (showSpinner) setLoading(true);
    else setRefreshing(true);
    try {
      const res = await fetch(
        `${API_URL}/admin/cloaking/recommendations?range=${range}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json: RecommendationsResponse = await res.json();
      setData(json);
    } catch (err) {
      console.error('[AdminCloakingRecommendations] fetch error:', err);
      toast.error(t('botCloaking.recommendations.loadFailed'));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchData(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range]);

  const stats = useMemo(() => {
    if (!data) {
      return {
        totalPages: 0,
        criticalPages: 0,
        globalCount: 0,
        topGlobalSeverity: null as Severity | null,
      };
    }
    let crit = 0;
    for (const p of data.pages) {
      if (p.recommendations[0]?.severity === 'critical') crit++;
    }
    return {
      totalPages: data.pages.length,
      criticalPages: crit,
      globalCount: data.globalOpportunities.length,
      topGlobalSeverity: data.globalOpportunities[0]?.severity || null,
    };
  }, [data]);

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-screen">
          <Activity className="w-12 h-12 text-purple-600 animate-spin" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-4 sm:p-6 lg:p-8 max-w-[1400px]">
        <button
          onClick={() => navigate('/admin/cloaking')}
          className="text-sm text-gray-600 hover:text-gray-900 inline-flex items-center gap-1 mb-4"
        >
          <ArrowLeft className="w-4 h-4" /> {t('botCloaking.recommendations.backToOverview')}
        </button>

        <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
              <Sparkles className="w-8 h-8 text-purple-600" />
              {t('botCloaking.recommendations.title')}
            </h1>
            <p className="text-gray-600 mt-1 text-sm max-w-2xl">
              {t('botCloaking.recommendations.subtitle')}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <RangePicker
              value={range}
              onChange={setRange}
              labels={{
                today: t('botCloaking.overview.range.today'),
                yesterday: t('botCloaking.overview.range.yesterday'),
                '7d': t('botCloaking.overview.range.7d'),
                '30d': t('botCloaking.overview.range.30d'),
              }}
            />
            <button
              type="button"
              onClick={() => fetchData(false)}
              disabled={refreshing}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-gray-300 bg-white hover:bg-gray-50 text-sm font-medium text-gray-700 disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
              {t('botCloaking.recommendations.refresh')}
            </button>
          </div>
        </div>

        {/* Stat cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <StatCard
            label={t('botCloaking.recommendations.stats.pagesNeedingTuning')}
            value={stats.totalPages}
            icon={<Layers className="w-7 h-7 text-purple-500" />}
            tone="purple"
          />
          <StatCard
            label={t('botCloaking.recommendations.stats.criticalPages')}
            value={stats.criticalPages}
            sub={t('botCloaking.recommendations.stats.criticalHint')}
            icon={<AlertTriangle className="w-7 h-7 text-rose-500" />}
            tone="rose"
          />
          <StatCard
            label={t('botCloaking.recommendations.stats.globalOpportunities')}
            value={stats.globalCount}
            sub={t('botCloaking.recommendations.stats.globalHint')}
            icon={<Settings className="w-7 h-7 text-amber-500" />}
            tone="amber"
          />
          <StatCard
            label={t('botCloaking.recommendations.stats.windowSize')}
            value={t(`botCloaking.overview.range.${range}`)}
            sub={
              data
                ? `${new Date(data.startDate).toLocaleDateString()} — ${new Date(
                    data.endDate,
                  ).toLocaleDateString()}`
                : ''
            }
            icon={<Activity className="w-7 h-7 text-emerald-500" />}
            tone="emerald"
          />
        </div>

        {/* Empty state */}
        {data && data.pages.length === 0 && data.globalOpportunities.length === 0 && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-12 text-center">
            <ShieldCheck className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
            <h2 className="text-lg font-semibold text-emerald-900 mb-1">
              {t('botCloaking.recommendations.empty.title')}
            </h2>
            <p className="text-sm text-emerald-800 max-w-md mx-auto">
              {t('botCloaking.recommendations.empty.body')}
            </p>
          </div>
        )}

        {/* Global opportunities */}
        {data && data.globalOpportunities.length > 0 && (
          <div className="mb-8">
            <SectionHeader
              icon={<Settings className="w-5 h-5 text-amber-600" />}
              title={t('botCloaking.recommendations.globalSection.title')}
              subtitle={t('botCloaking.recommendations.globalSection.subtitle')}
            />
            <div className="space-y-3">
              {data.globalOpportunities.map((op) => (
                <GlobalOpportunityCard
                  key={op.key}
                  opportunity={op}
                  onApply={() =>
                    navigate(`/admin/cloaking?settings=1&focus=${encodeURIComponent(op.key)}`)
                  }
                />
              ))}
            </div>
          </div>
        )}

        {/* Per-page recommendations */}
        {data && data.pages.length > 0 && (
          <div>
            <SectionHeader
              icon={<Lightbulb className="w-5 h-5 text-purple-600" />}
              title={t('botCloaking.recommendations.perPageSection.title')}
              subtitle={t('botCloaking.recommendations.perPageSection.subtitle')}
            />
            <div className="space-y-4">
              {data.pages.map((p) => (
                <PageRecommendationCard
                  key={p.id}
                  page={p}
                  onOpen={() => navigate(`/admin/cloaking/${p.id}`)}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

// ---------------- subcomponents ----------------

function SectionHeader({
  icon,
  title,
  subtitle,
}: {
  icon: React.ReactNode;
  title: string;
  subtitle: string;
}) {
  return (
    <div className="mb-3 flex items-start gap-2">
      {icon}
      <div>
        <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
        <p className="text-sm text-gray-600">{subtitle}</p>
      </div>
    </div>
  );
}

function StatCard({
  label,
  value,
  sub,
  icon,
  tone,
}: {
  label: string;
  value: string | number;
  sub?: string;
  icon: React.ReactNode;
  tone: 'purple' | 'rose' | 'amber' | 'emerald';
}) {
  const toneCls: Record<typeof tone, string> = {
    purple: 'border-purple-100 bg-purple-50/40',
    rose: 'border-rose-100 bg-rose-50/40',
    amber: 'border-amber-100 bg-amber-50/40',
    emerald: 'border-emerald-100 bg-emerald-50/40',
  };
  return (
    <div className={`bg-white border ${toneCls[tone]} rounded-xl p-5`}>
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="text-xs uppercase tracking-wide text-gray-500 font-medium">{label}</p>
          <p className="text-3xl font-bold text-gray-900 mt-1 truncate">{value}</p>
          {sub && <p className="text-xs text-gray-500 mt-0.5 truncate">{sub}</p>}
        </div>
        {icon}
      </div>
    </div>
  );
}

function GlobalOpportunityCard({
  opportunity,
  onApply,
}: {
  opportunity: GlobalOpportunity;
  onApply: () => void;
}) {
  const { t } = useTranslation('admin');
  const sevCls = severityClass(opportunity.severity);

  // Render the suggested value differently for booleans vs numbers.
  const renderValue = (v: unknown): string => {
    if (typeof v === 'boolean') {
      return v
        ? t('botCloaking.recommendations.value.on')
        : t('botCloaking.recommendations.value.off');
    }
    if (v === null || v === undefined) return '—';
    return String(v);
  };

  return (
    <div className={`border rounded-xl overflow-hidden ${sevCls.cardBorder}`}>
      <div className={`px-5 py-4 ${sevCls.headerBg}`}>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <SeverityBadge severity={opportunity.severity} />
              <code className="text-xs px-2 py-0.5 rounded bg-white/80 border border-gray-200 text-gray-700 font-mono">
                {opportunity.key}
              </code>
            </div>
            <p className="text-sm text-gray-800">
              {t(`botCloaking.recommendations.knob.${opportunity.key}.title`, {
                defaultValue: opportunity.key,
              })}
            </p>
            <p className="text-xs text-gray-600 mt-1">
              {t(`botCloaking.recommendations.knob.${opportunity.key}.why`, {
                defaultValue: t('botCloaking.recommendations.genericWhy', {
                  count: opportunity.affectedPages,
                  evidence: opportunity.totalEvidence,
                }),
                count: opportunity.affectedPages,
                evidence: opportunity.totalEvidence,
              })}
            </p>
          </div>
          <button
            type="button"
            onClick={onApply}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-purple-600 hover:bg-purple-700 text-white text-sm font-semibold whitespace-nowrap"
          >
            {t('botCloaking.recommendations.openEditor')}
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="px-5 py-4 bg-white">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
          <Metric
            label={t('botCloaking.recommendations.field.current')}
            value={renderValue(opportunity.currentValue)}
          />
          <Metric
            label={t('botCloaking.recommendations.field.suggested')}
            value={renderValue(opportunity.suggestedValue)}
            tone="purple"
          />
          <Metric
            label={t('botCloaking.recommendations.field.affectedPages')}
            value={opportunity.affectedPages.toLocaleString()}
          />
          <Metric
            label={t('botCloaking.recommendations.field.evidence')}
            value={opportunity.totalEvidence.toLocaleString()}
            sub={t('botCloaking.recommendations.field.evidenceSub')}
          />
        </div>

        {opportunity.pages.length > 0 && (
          <details className="text-sm">
            <summary className="cursor-pointer text-purple-700 hover:text-purple-900 font-medium">
              {t('botCloaking.recommendations.field.affectedListLabel', {
                count: opportunity.pages.length,
              })}
            </summary>
            <ul className="mt-2 space-y-1 pl-4 border-l-2 border-purple-100">
              {opportunity.pages.slice(0, 25).map((p) => (
                <li
                  key={p.id}
                  className="flex items-center justify-between py-1 text-xs"
                >
                  <span className="text-gray-700 truncate">
                    {p.name}{' '}
                    <span className="text-gray-400">({p.ownerEmail || 'unknown'})</span>
                  </span>
                  <span className="text-gray-500 tabular-nums whitespace-nowrap">
                    {p.evidenceCount.toLocaleString()}{' '}
                    {t('botCloaking.recommendations.field.blocksShort')}
                  </span>
                </li>
              ))}
            </ul>
          </details>
        )}
      </div>
    </div>
  );
}

function PageRecommendationCard({
  page,
  onOpen,
}: {
  page: PageRow;
  onOpen: () => void;
}) {
  const { t } = useTranslation('admin');
  const topSev = page.recommendations[0]?.severity || 'low';
  const sevCls = severityClass(topSev);

  return (
    <div
      className={`bg-white border rounded-xl overflow-hidden hover:shadow-md transition-shadow cursor-pointer ${sevCls.cardBorder}`}
      onClick={onOpen}
    >
      <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-0.5">
            <SeverityBadge severity={topSev} />
            <h3 className="text-base font-semibold text-gray-900 truncate">{page.name}</h3>
          </div>
          <p className="text-xs text-gray-500 truncate">
            {page.ownerName} · {page.ownerEmail} · {page.slug || page.id.slice(0, 8)}
          </p>
        </div>
        <div className="flex items-center gap-3 whitespace-nowrap">
          <div className="text-right text-xs">
            <p className="text-gray-500">
              {t('botCloaking.recommendations.field.blockRate')}
            </p>
            <p className="font-semibold text-gray-900 tabular-nums">
              {(page.summary.blockRate * 100).toFixed(1)}%
            </p>
          </div>
          <div className="text-right text-xs">
            <p className="text-gray-500">{t('botCloaking.recommendations.field.fps')}</p>
            <p className="font-semibold text-amber-700 tabular-nums">
              {page.summary.falsePositiveCount}
            </p>
          </div>
          <ChevronRight className="w-4 h-4 text-gray-400" />
        </div>
      </div>

      <ul className="divide-y divide-gray-100">
        {page.recommendations.map((rec) => (
          <li key={rec.id} className="px-5 py-3 flex items-start gap-3">
            <span
              className={`mt-1 inline-block w-2 h-2 rounded-full flex-shrink-0 ${
                severityClass(rec.severity).dot
              }`}
            />
            <div className="flex-1 min-w-0">
              <p className="text-sm text-gray-800">
                {t(`botCloaking.recommendations.rationale.${rec.rationaleKey}`, {
                  defaultValue: rec.rationaleKey,
                  ...rec.rationaleVars,
                })}
              </p>
              <div className="text-xs text-gray-500 mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
                {rec.key && (
                  <code className="px-1.5 py-0.5 rounded bg-gray-100 text-gray-700 font-mono">
                    {rec.key}
                  </code>
                )}
                {rec.direction !== 'review' && rec.suggestedValue !== null && (
                  <span>
                    <span className="text-gray-400">
                      {t('botCloaking.recommendations.field.now')}:
                    </span>{' '}
                    <span className="font-medium text-gray-700 tabular-nums">
                      {formatValue(rec.currentValue)}
                    </span>{' '}
                    <span className="text-gray-400">→</span>{' '}
                    <span className="font-semibold text-purple-700 tabular-nums">
                      {formatValue(rec.suggestedValue)}
                    </span>
                  </span>
                )}
                <span>
                  {t('botCloaking.recommendations.field.evidence')}:{' '}
                  <span className="font-semibold tabular-nums">{rec.evidenceCount}</span>{' '}
                  <span className="text-gray-400">
                    ({(rec.evidencePct * 100).toFixed(0)}% of blocks)
                  </span>
                </span>
                <span className="text-gray-400">
                  · {t('botCloaking.recommendations.field.confidence')}:{' '}
                  {t(`botCloaking.recommendations.confidence.${rec.confidence}`)}
                </span>
              </div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

function Metric({
  label,
  value,
  sub,
  tone,
}: {
  label: string;
  value: string;
  sub?: string;
  tone?: 'purple';
}) {
  return (
    <div>
      <p className="text-xs text-gray-500 uppercase tracking-wide">{label}</p>
      <p
        className={`mt-0.5 text-xl font-bold tabular-nums ${
          tone === 'purple' ? 'text-purple-700' : 'text-gray-900'
        }`}
      >
        {value}
      </p>
      {sub && <p className="text-xs text-gray-400">{sub}</p>}
    </div>
  );
}

function SeverityBadge({ severity }: { severity: Severity }) {
  const { t } = useTranslation('admin');
  const cls: Record<Severity, string> = {
    critical: 'bg-rose-100 text-rose-800 border-rose-200',
    high: 'bg-orange-100 text-orange-800 border-orange-200',
    medium: 'bg-amber-100 text-amber-800 border-amber-200',
    low: 'bg-slate-100 text-slate-700 border-slate-200',
  };
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide border ${cls[severity]}`}
    >
      {t(`botCloaking.recommendations.severity.${severity}`)}
    </span>
  );
}

function severityClass(s: Severity) {
  switch (s) {
    case 'critical':
      return {
        cardBorder: 'border-rose-200',
        headerBg: 'bg-rose-50',
        dot: 'bg-rose-500',
      };
    case 'high':
      return {
        cardBorder: 'border-orange-200',
        headerBg: 'bg-orange-50',
        dot: 'bg-orange-500',
      };
    case 'medium':
      return {
        cardBorder: 'border-amber-200',
        headerBg: 'bg-amber-50',
        dot: 'bg-amber-500',
      };
    case 'low':
    default:
      return {
        cardBorder: 'border-slate-200',
        headerBg: 'bg-slate-50',
        dot: 'bg-slate-400',
      };
  }
}

function formatValue(v: unknown): string {
  if (typeof v === 'boolean') return v ? 'on' : 'off';
  if (v === null || v === undefined) return '—';
  if (Array.isArray(v)) return v.join(', ');
  return String(v);
}

function RangePicker({
  value,
  onChange,
  labels,
}: {
  value: Range;
  onChange: (r: Range) => void;
  labels: Record<Range, string>;
}) {
  const opts: Range[] = ['today', 'yesterday', '7d', '30d'];
  return (
    <div className="inline-flex rounded-lg border border-gray-300 bg-white p-1 text-sm">
      {opts.map((o) => (
        <button
          key={o}
          type="button"
          onClick={() => onChange(o)}
          className={`px-3 py-1.5 rounded-md font-medium transition-colors ${
            value === o ? 'bg-purple-600 text-white' : 'text-gray-700 hover:bg-gray-100'
          }`}
        >
          {labels[o]}
        </button>
      ))}
    </div>
  );
}
